<script setup lang="ts">
/**
 * Step 3 · Shape (plan §3 step 3, §4.4): make it your own. Controls are generated from the
 * family's parameter metadata (`FamilyDefinition.parameters`, in `ui.group` order); every edit
 * re-runs the family generator, so model, parts and hash update together.
 */
import { computed } from 'vue';
import { bayCount, errorFor, fmtMm, layoutOf } from '@/domain';
import type { ParameterDef } from '@/families/types';
import { FAMILIES, familyOf } from '@/families/registry';
import Icon from './Icon.vue';
import StepPanel from './StepPanel.vue';
import PanelHead from './PanelHead.vue';
import ParamControl from './ParamControl.vue';
import { app, snap, ui } from '@/app/state';
import { announce, toast } from '@/app/announce';
import { choose } from '@/app/analysis';
import { bayField, rename, setEvenTo, setProps } from '@/app/edits';
import { applyIssueFix, designFix, showProblem } from '@/app/fixes';
import { changeLabel, focusSoon, go, nextRevId } from '@/app/navigation';
import { paramValue } from '@/app/param-value';
import { gridBaseOf } from '@/families/grid-bookcase/design';

const d = computed(() => snap.value.design);
const b = computed(() => snap.value.build);
const fam = computed(() => snap.value.family);
const bays = computed(() => bayCount(d.value));
const bay = computed(() => Math.min(ui.bay, bays.value - 1));

/** Is a parameter shown? (its own step, and `showWhen` against the design.) */
function shown(p: ParameterDef): boolean {
  if (p.ui.step === 'look') return false;
  const w = p.ui.showWhen;
  if (!w) return true;
  const v = paramValue(d.value, w.key, bays.value);
  if ('notEquals' in w) return v !== w.notEquals;
  if ('equals' in w) return v === w.equals;
  return true;
}
const groups = computed(() => {
  const out: { name: string; id: string; params: ParameterDef[] }[] = [];
  for (const p of fam.value.parameters) {
    if (!shown(p)) continue;
    let g = out.find((x) => x.name === p.ui.group);
    if (!g) { g = { name: p.ui.group, id: `grp-${p.ui.group.replace(/\W/g, '')}`, params: [] }; out.push(g); }
    g.params.push(p);
  }
  return out;
});

/** What lifts the bottom off the floor, for the clear-space sum (plinth, feet, or the gap under a raised bottom). */
const liftText = computed(() => {
  const base = gridBaseOf(d.value);
  if (base.kind === 'none') return '';
  const what = base.kind === 'plinth' ? 'plinth' : base.kind === 'feet' ? 'feet' : 'gap under the bottom';
  return ` − ${what} ${fmtMm(base.height)}`;
});
const dimErrs = computed(() => (['W', 'H', 'D'] as const).map((k) => ({ k, msg: ui.fieldErrors[k] })).filter((x) => x.msg));
const summary = computed(() => b.value.bays[bay.value] ?? b.value.bays[0]!);
const sumOk = computed(() => summary.value.remainder === 0);
/** The openings sum rule belongs to families with an openings list (the grid). */
const hasOpenings = computed(() => fam.value.parameters.some((p) => p.type === 'perOpening'));
const opErr = computed(() => errorFor(b.value, bayField(bay.value, 'openings')));
const handled = (f: string) => ['openings', 'W', 'H', 'D', 'bayWidths'].includes(f) || /(^|\/)opening-\d+$/.test(f) || /^bay-\d+\/openings$/.test(f);
const otherErrs = computed(() => b.value.validation.errors.filter((e) => !handled(e.field)));
const bayErr = computed(() => errorFor(b.value, 'bayWidths'));
const warns = computed(() => b.value.validation.warnings);
/** While the design is invalid the dock's primary action is the first error's fix (PLAN §3, §6). */
const fix = computed(() => { void snap.value; return designFix(); });
const r = computed(() => snap.value.stockInfo);
const c = computed(() => snap.value.construction);
const tMm = computed(() => fmtMm(r.value.thicknessTicks));
const n = computed(() => layoutOf(d.value, bay.value).n);
const nameDraft = computed(() => ('designName' in ui.drafts ? ui.drafts.designName! : d.value.appearance?.name ?? ''));
const tag = computed(() => (!snap.value.provisional ? 'Reviewed' : snap.value.selfAuthored ? `Self-authored · v${c.value.version}` : 'Provisional'));

function onName(e: Event) {
  const el = e.target as HTMLInputElement;
  if (!el.isConnected) return;
  delete ui.drafts.designName;
  rename(el.value);
}
function onProps(e: Event) { focusSoon('#propsSw'); setProps((e.target as HTMLInputElement).checked); }
/** Advice can carry a one-click change too (e.g. grid: "Add a divider (2 bays)" for a long shelf span). */
function applyAdvice(w: { field: string; fix?: { label: string; patch: Record<string, unknown> } }) {
  if (!w.fix) return;
  applyIssueFix(w.fix);
  focusSoon('#panelTitle');
}
function applyFix() {
  const f = fix.value;
  if (!f) return;
  f.act();
  if (f.kind === 'show') return;
  // focus follows: Continue once valid, else the next fix
  focusSoon(app.store.build.validation.ok ? '#dock [data-go="look"]' : '#dock [data-act="fix"]');
}
function commit() {
  const rv = app.store.commitRevision(changeLabel());
  toast(`Saved as ${rv.id}.`);
  announce(`Saved as revision ${rv.id}.`);
  focusSoon('#dock [data-go="look"]');
}
/** Change style (ui-spec §2.3): a new working design from the chosen family's defaults, undoable. */
function changeStyle() {
  choose({
    title: 'Change style',
    lede: 'Edits inside the current style won’t carry over, except size and material. Undo brings them back.',
    choices: FAMILIES.map((f) => ({
      label: f.definition.label, sub: f.definition.description, primary: f.definition.id === familyOf(d.value).definition.id,
      act: () => {
        const keep = app.store.design;
        const next = { ...f.create(), W: keep.W, H: keep.H, D: keep.D, appearance: { ...keep.appearance } };
        app.store.apply(() => next, { label: `Style changed to ${f.definition.label}` });
        ui.bay = 0;
        announce(`Style changed to ${f.definition.label}. Undo to go back.`);
      },
    })),
  });
}
</script>

<template>
  <StepPanel>
    <PanelHead :n="3" :title="'Shape your version'" :sub="fam.label" lede="Drag a shelf on the model, or type exact sizes here. The model, sizes and parts update together." />
    <div class="row-acts row-acts--tight"><button type="button" class="btn btn--sm btn--quiet" data-act="change-style" @click="changeStyle"><Icon name="history" /> Change style</button></div>

    <section v-for="g in groups" :key="g.id" class="sec" :aria-labelledby="g.id" :data-group="g.name">
      <h2 :id="g.id" class="h-sec">{{ g.name }}</h2>
      <div :class="{ dims: g.name === 'Overall size' }">
        <ParamControl v-for="p in g.params" :key="p.key" :p="p" :hide-label="p.ui.label === g.name" />
      </div>
      <template v-if="g.name === 'Overall size'">
        <p v-for="e in dimErrs" :id="`err-${e.k}`" :key="e.k" class="field-err" role="alert"><Icon name="alert" /><span>{{ e.msg }}</span></p>
      </template>
      <p v-if="g.name === 'Bays' && bayErr" class="field-err"><Icon name="alert" /><span>{{ bayErr }}</span></p>
    </section>

    <div v-if="otherErrs.length" id="shapeErrors" class="notes notes--err" tabindex="-1">
      <p v-for="e in otherErrs" :key="e.field + e.message"><Icon name="alert" /><span>{{ e.message }}</span></p>
    </div>
    <div v-if="warns.length" class="notes" role="group" aria-label="Advice">
      <p v-for="w in warns" :key="w.field + w.message"><Icon name="alert" /><span>{{ w.message }}<button v-if="w.fix" type="button" class="btn btn--sm btn--quiet note-fix" :data-warn-fix="w.field" @click="applyAdvice(w)">{{ w.fix.label }}</button></span></p>
    </div>
    <section class="sec" aria-labelledby="stockH">
      <div class="sec-head"><h2 id="stockH" class="h-sec">Material and construction</h2><span class="tag tag--prov">{{ tag }}</span></div>
      <dl class="kv">
        <div><dt>Carcass</dt><dd>{{ r.label }}, measured {{ (r.thicknessTicks / 10).toFixed(1) }} mm</dd></div>
        <div v-if="b.thickness.back"><dt>Back</dt><dd>{{ r.back.label }}</dd></div>
        <div><dt>Stock</dt><dd v-if="r.sheet">{{ fmtMm(r.sheet.lengthTicks) }} × {{ fmtMm(r.sheet.widthTicks) }} mm sheets, grain along the length</dd><dd v-else>Solid boards up to {{ fmtMm(r.maxWidthTicks ?? 0) }} mm wide; wider parts are glued up</dd></div>
        <div><dt>Joints</dt><dd>{{ c.name }} · v{{ c.version }}</dd></div>
      </dl>
      <p class="muted small">{{ snap.selfAuthored ? 'Construction rules are self-authored and not maker-reviewed. Sizes are exact; joints, fixings and loads are a starting point.' : snap.provisional ? 'Read-only for now. The profile stays provisional until your maker records a review with a name and date.' : `Reviewed by ${c.reviewer}, ${c.reviewedAt}. Read-only.` }} Change the material in Look.</p>
    </section>
    <section class="sec" aria-labelledby="lookH">
      <h2 id="lookH" class="h-sec">Appearance <span class="muted">never changes what gets cut</span></h2>
      <label class="lbl" for="designName">Name</label>
      <input id="designName" class="input" autocomplete="off" :value="nameDraft" @input="ui.drafts.designName = ($event.target as HTMLInputElement).value" @change="onName">
      <label class="switch"><input id="propsSw" type="checkbox" role="switch" :checked="d.appearance?.props !== false" @change="onProps"><span class="sw" aria-hidden="true" /><span>Show books and objects</span></label>
    </section>
    <div v-if="hasOpenings" id="sumRule" class="sum sum--sticky" :class="sumOk ? 'is-ok' : 'is-err'" role="status">
      <p class="sum-main"><Icon :name="sumOk ? 'check' : 'alert'" /><span>{{ sumOk ? `${bays > 1 ? `Bay ${bay + 1} openings` : 'Openings'} ${fmtMm(summary.openingsSum)} of ${fmtMm(summary.available)} mm · all placed` : opErr }}</span></p>
      <p class="sum-math">Clear space: height {{ fmtMm(d.H) }}{{ liftText }} − {{ n + 2 }} panels × {{ tMm }} = <strong>{{ fmtMm(summary.available) }} mm</strong></p>
      <button v-if="!sumOk" type="button" class="btn btn--sm btn--quiet" data-act="even-on" @click="setEvenTo(true, bay)">Space evenly instead</button>
    </div>

    <template #dock>
      <template v-if="fix">
        <p class="dock-note dock-note--err" data-fix-reason><Icon name="alert" /> {{ fix.reason }}</p>
        <div class="dock-acts">
          <button v-if="fix.kind !== 'show'" type="button" class="btn btn--quiet btn--sm" data-act="show-problem" @click="showProblem()">Show the problem</button>
          <button type="button" class="btn btn--ink btn--lg" data-act="fix" :data-fix="fix.kind" @click="applyFix">{{ fix.label }}</button>
        </div>
      </template>
      <template v-else>
        <button type="button" class="btn btn--quiet" data-act="commit" :disabled="!snap.isDirty" :title="snap.isDirty ? 'Freeze these edits as a new revision' : 'No unsaved edits'" @click="commit">
          <Icon name="save" /> {{ snap.isDirty ? `Save as ${nextRevId()}` : `${snap.currentRevisionId || 'Saved'} saved` }}
        </button>
        <button type="button" class="btn btn--ink btn--lg" data-go="look" @click="go('look')">Continue to Look <Icon name="arrow" /></button>
      </template>
    </template>
  </StepPanel>
</template>
